import { HSpacer, InfoTooltip, Text } from '@/components/DesignSystem';
import { FilterBar } from '@/components/DesignSystem/Toolbar/FilterBar';
import {
  Filter,
  FilterResult,
  FilterSelection,
  FilterSelections,
} from '@/components/DesignSystem/Toolbar/interfaces';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Stack } from '@mui/material';
import { formatNumber } from '@shared/utilities';
import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from '../Search/Search';
import { VSpacer } from '../Spacer';

interface ToolbarProps {
  filters?: Filter[],
  filterSelections?: FilterSelections,
  hideAllFilter?: boolean,
  initialSearch?: string | null,
  initialSelections?: FilterSelections,
  infoTooltip?: {
    labelText: string,
    tooltipText: string,
    visible: boolean,
  },
  onChange?: (result: FilterResult) => void,
  retainSelectionsOnFilterChange?: boolean,
  searchPlaceholder?: string,
  showClearAllButton?: boolean,
  testID: string,
  totalItems?: number,
  totalUnitLabel?: { singular: string, plural: string },
  themeColor?: string,
}

interface SearchVariation extends ToolbarProps {
  hideFilter?: never,
  hideSearch?: boolean,
}

interface FilterVariation extends ToolbarProps {
  hideFilter?: boolean,
  hideSearch?: never,
}

export const Toolbar = ({
  filters = [],
  filterSelections,
  hideFilter = false,
  hideSearch = false,
  hideAllFilter = true,
  initialSearch,
  initialSelections,
  infoTooltip,
  onChange,
  retainSelectionsOnFilterChange = false,
  searchPlaceholder,
  showClearAllButton = false,
  testID,
  totalItems,
  totalUnitLabel,
  themeColor,
}: SearchVariation | FilterVariation) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const [search, setSearch] = useState(initialSearch ?? '');
  const [_filterSelections, _setFilterSelections]
    = useState<FilterSelections>(filterSelections ?? initialSelections ?? new Map());

  const stringifiedFilters = JSON.stringify(filters);
  useEffect(() => {
    if (!filterSelections && !initialSelections && !retainSelectionsOnFilterChange) {
      const defaultSelections = new Map<string, FilterSelection>(
        filters.map((filter) => (
          [
            filter.id,
            new Set(filter.options.filter((option) => option.default).map(({ id }) => id)),
          ]
        )),
      );
      _setFilterSelections(defaultSelections);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelections, stringifiedFilters]);

  useEffect(() => {
    if (filterSelections) {
      _setFilterSelections(filterSelections);
    }
  }, [filterSelections]);

  const getFilterResult = (
    search: string,
    selections: Map<string, FilterSelection>,
  ) => {
    const result: FilterResult = {};
    if (search !== '') {
      result.search = search.toLowerCase();
    }

    if (selections.size) {
      result.selections = new Map();
      Array.from(selections.entries(), ([filterId, selection]) => {
        if (selection.size) {
          result.selections?.set(filterId, new Set(Array.from(selection)));
        }
      });
    }
    return result;
  };

  const stringifiedSelections = JSON.stringify(
    Array.from(_filterSelections).map(([, values]) => Array.from(values)),
  );

  useEffect(() => {
    onChange?.(getFilterResult(search, _filterSelections));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, stringifiedSelections]);

  const singularUnitLabel = totalUnitLabel?.singular ?? t($ => $.common.units.item);
  const pluralUnitLabel = totalUnitLabel?.plural ?? t($ => $.common.units.items);

  return (
    <Stack data-testid={testID}>
      {!hideSearch && (
        <>
          <Search
            fullWidth={isMobile}
            onChangeText={setSearch}
            placeholder={searchPlaceholder ?? t($ => $.common.search.placeholder)}
            testID={`${testID}-search`}
            value={search}
            width={!isMobile ? 380 : undefined}
          />
          <VSpacer size="6" />
        </>
      )}
      {!hideFilter && !!filters.length && (
        <FilterBar
          filters={filters}
          hideAllFilter={hideAllFilter}
          onChange={_setFilterSelections}
          selections={_filterSelections}
          showClearAllButton={showClearAllButton}
          testID={`${testID}-filter`}
          themeColor={themeColor}
        />
      )}
      {totalItems !== undefined && (
        <>
          <VSpacer size="7" />
          <Stack direction="row">
            <Text category="body-medium">
              {formatNumber(totalItems, i18next)}{' '}
              {totalItems === 1 ? singularUnitLabel : pluralUnitLabel}
            </Text>
            <HSpacer size="7" />
            {!!infoTooltip?.visible && (
              <Stack alignItems="center" direction="row">
                <Text category="title-small" color="info">
                  {infoTooltip.labelText}
                </Text>
                <HSpacer size="3"/>
                <InfoTooltip color="info">
                  {infoTooltip.tooltipText}
                </InfoTooltip>
              </Stack>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};
